import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { Label } from '@reflex/ui/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@reflex/ui/components/ui/select';
import { usePermission } from '@reflex/ui/client/permissions';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type { InviteShare } from './inviteShare';
import { invalidInviteEmails, parseInviteEmails } from './inviteEmails';

/**
 * Sentinel for "no extra role". `Select` cannot hold an empty-string value
 * (Radix reserves it for the placeholder), and the wire format for member-only
 * is an absent `roleId`, so the sentinel never leaves this file.
 */
const MEMBER_ONLY = 'member-only';

/**
 * The built-in role every new member already receives. Offering it as a
 * choice would put two options in the list that do exactly the same thing,
 * one of them named "Member" beside a default also named "Member".
 */
const BUILTIN_MEMBER_ROLE_ID = 'builtin-org-member';

const inviteFormSchema = z.object({
  roleId: z.string(),
  emails: z.string().superRefine((value, ctx) => {
    if (parseInviteEmails(value).length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Enter at least one email address' });
      return;
    }
    const invalid = invalidInviteEmails(value);
    if (invalid.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Enter valid email addresses: ${invalid.join(', ')}`,
      });
    }
  }),
});
type InviteFormValues = z.infer<typeof inviteFormSchema>;

interface InviteEmailFormProps {
  organizationId: string;
  /**
   * Called with the freshly minted links once at least one invite succeeded.
   * The form does not show them itself: the callers put them in different
   * places (a standalone dialog on the admin panel, this dialog's own body in
   * the Add member flow), and only the caller knows which is safe.
   */
  onInvited: (shares: InviteShare[]) => void;
  /**
   * `data-tutorial-id` for the send button. Only the org-facing members page
   * passes one: the members tutorial resolves its target with a
   * document-wide selector, so a second element carrying the same hook would
   * make which one it spotlights depend on DOM order.
   */
  tutorialId?: string;
}

/**
 * The invite-by-email send form.
 *
 * The invitee does not need an account. The server reserves the address and
 * emails a redemption link (when the deployment has email configured); the
 * invite is claimed after they sign up with that address, so the caller's
 * share dialog always exposes the link as the fallback delivery channel.
 */
export function InviteEmailForm({ organizationId, onInvited, tutorialId }: InviteEmailFormProps) {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.organizationInvites(organizationId);

  // Same gate the server applies: choosing a role is a role grant, so
  // `members:invite` alone gets the plain form and every invitee lands as a
  // member. `usePermission`, not `can`, to inherit the platform-admin bypass
  // the POST itself honours.
  const canAssignRoles = usePermission('roles:manage');

  const rolesQuery = useQuery({
    queryKey: ['organizations', organizationId, 'roles'],
    queryFn: () => api.listOrganizationRoles(organizationId),
    enabled: organizationId.length > 0 && canAssignRoles,
  });
  // The API already hides platform-admin and sharing roles; team-scoped roles
  // are dropped here because org membership is not team membership, and the
  // built-in member role because it is the default, not an addition.
  const assignableRoles = useMemo(
    () =>
      (rolesQuery.data ?? []).filter(
        (role) => role.scope === 'org' && role.id !== BUILTIN_MEMBER_ROLE_ID,
      ),
    [rolesQuery.data],
  );

  const inviteForm = useForm<InviteFormValues>({
    resolver: zodResolver(inviteFormSchema),
    defaultValues: { emails: '', roleId: MEMBER_ONLY },
  });

  const inviteMutation = useMutation({
    mutationFn: async ({ emails, roleId }: { emails: string[]; roleId?: string }) => {
      const results = await Promise.allSettled(
        emails.map((email) => api.createOrganizationInvite(organizationId, email, roleId)),
      );
      const created: InviteShare[] = [];
      const failed: string[] = [];
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          created.push({
            invite: result.value.invite,
            redeemUrl: result.value.redeemUrl,
            emailSent: result.value.emailSent,
            reissued: result.value.reissued,
          });
        } else {
          failed.push(emails[index]);
        }
      });
      return { created, failed };
    },
    onSuccess: ({ created, failed }) => {
      void queryClient.invalidateQueries({ queryKey });
      if (created.length > 0) {
        onInvited(created);
        const emailed = created.filter((share) => share.emailSent).length;
        if (emailed > 0) {
          toast.success(
            emailed === 1 ? 'Invite email sent' : `${emailed} invite emails sent`,
            emailed === created.length
              ? undefined
              : { description: 'Share the link for the rest.' },
          );
        }
      }
      // Keep the addresses that failed in the field so they can be retried,
      // along with the role they were meant to get; clear the field entirely
      // only when every invite succeeded.
      inviteForm.reset({ emails: failed.join(', '), roleId: inviteForm.getValues('roleId') });
      if (failed.length > 0) {
        toast.error(
          failed.length === 1
            ? 'Failed to send 1 invite'
            : `Failed to send ${failed.length} invites`,
          { description: `Could not invite ${failed.join(', ')}.` },
        );
      }
    },
    // No `onError`: `mutationFn` settles every request itself and always
    // resolves, so a per-address failure surfaces through `failed` above.
  });

  const onInviteSubmit = inviteForm.handleSubmit((values) => {
    inviteMutation.mutate({
      emails: parseInviteEmails(values.emails),
      roleId: values.roleId === MEMBER_ONLY ? undefined : values.roleId,
    });
  });

  return (
    <div data-testid="invite-by-email-section">
      <form className="flex flex-col gap-3" onSubmit={onInviteSubmit}>
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="min-w-0 flex-1">
            <Input
              type="text"
              placeholder="teammate@example.com, another@example.com"
              aria-label="Emails to invite"
              disabled={inviteMutation.isPending}
              {...inviteForm.register('emails')}
              data-testid="invite-email-input"
            />
            {inviteForm.formState.errors.emails && (
              <p className="mt-1 text-xs text-destructive">
                {inviteForm.formState.errors.emails.message}
              </p>
            )}
          </div>
          <Button
            type="submit"
            className="shrink-0"
            disabled={inviteMutation.isPending}
            data-tutorial-id={tutorialId}
          >
            {inviteMutation.isPending ? 'Sending…' : 'Send invite'}
          </Button>
        </div>

        {canAssignRoles && (
          <div className="space-y-2" data-testid="invite-role-section">
            <Label htmlFor="invite-role">Role</Label>
            {/*
             * Controlled through the form so a partial failure keeps the role
             * beside the addresses it still has to be applied to. Every
             * invitee is a member regardless; this only adds a role on top.
             */}
            <Controller
              control={inviteForm.control}
              name="roleId"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={inviteMutation.isPending}
                >
                  <SelectTrigger
                    id="invite-role"
                    aria-label="Role"
                    data-testid="invite-role-select"
                    onBlur={field.onBlur}
                    ref={field.ref}
                  >
                    <SelectValue placeholder={rolesQuery.isLoading ? 'Loading roles…' : 'Member'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={MEMBER_ONLY}>Member</SelectItem>
                    {assignableRoles.map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        )}
      </form>
      <p className="mt-2 text-xs text-muted-foreground">
        Separate multiple emails with a comma or space. People without an account get a link to sign
        up with the invited address.
        {canAssignRoles ? ' The role is applied when they redeem the invite.' : ''}
      </p>
    </div>
  );
}
